import { useCallback, useEffect, useRef, useState } from "react";
import { api, unwrap } from "@/lib/api";

export function useAppointments({ branchId, intervalMs = 5000, paused = false } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timer = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const res = await api.get("/appointments/poll", { params });
      const data = unwrap(res);
      setItems(data?.appointments || []);
      setLastUpdated(data?.last_updated || new Date().toISOString());
    } catch (e) {
      // silent; will retry on next tick
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchOnce();
    if (!paused) {
      timer.current = setInterval(fetchOnce, intervalMs);
    }
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [fetchOnce, intervalMs, paused]);

  const refresh = fetchOnce;

  const create = async (payload) => {
    const res = await api.post("/appointments", payload);
    await fetchOnce();
    return unwrap(res);
  };

  const update = async (id, patch) => {
    const res = await api.patch(`/appointments/${id}`, patch);
    await fetchOnce();
    return unwrap(res);
  };

  const remove = async (id) => {
    const res = await api.delete(`/appointments/${id}`);
    await fetchOnce();
    return unwrap(res);
  };

  return { items, loading, lastUpdated, refresh, create, update, remove };
}
